import { classNames } from "@/data/classnames";
import { useStudioStream } from "@/data/studio-react/useStudioStream";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Label,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import {
  ArrowDownTrayIcon,
  CheckIcon,
  ChevronUpDownIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/20/solid";
import {
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentIcon,
  PrinterIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";

import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { useSaleAttributeControllers } from "@/data/attribute-definitions/attributeControls";
import { GridSelection } from "@glideapps/glide-data-grid";
import {
  Fragment,
  MutableRefObject,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { toast } from "sonner";

// JSPDF
import { formatAsCurrency } from "@/data/amounts";
import { getLotsInRange, sortByLotNumber } from "@/data/lots";
import { type Styles } from "jspdf-autotable";
import downloadPDF from "../../../../documents/report-docs/sales/client-side";

import useStudio from "@/data/studio-react/useStudio";
import { Timestamp } from "firebase/firestore";
import { Lot, Printer, Sale } from "types";
import { Spinner } from "../../../Buttons";

interface PriceEntryDialogProps {
  gridSelection: GridSelection;
  filteredLotsRef: MutableRefObject<Lot[]>;
  close: () => void;
  isOpen: boolean;
  sale: Sale | null;
}

// Get base lot fields
export const lotBaseFields = [
  "lotNumber",
  "productCode",
  "remarks",
  "unitOfSale",
  "sellerName",
  "sellerAccount",
  "buyerName",
  "buyerAccount",
  "generated.description",

  "unitPriceInCents",
  "reservePriceInCents",
  "startingPriceInCents",
];

export function PriceEntryDialog(props: PriceEntryDialogProps) {
  let { gridSelection, filteredLotsRef, isOpen, close, sale } = props;
  let [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  let [availableColumns, setAvailableColumns] = useState<string[]>([]);
  let [lotStart, setLotStart] = useState("");
  let [lotEnd, setLotEnd] = useState("");
  let [isRangeEnabled, setIsRangeEnabled] = useState(false);
  let [selectedPrinter, setSelectedPrinter] = useState<Printer | null>(null);
  let [isLoading, setIsLoading] = useState(false);
  let [isDoubleSpaced, setIsDoubleSpaced] = useState(true);

  // Get the printers list
  let marketId = useStudioStream("session:marketId");
  let printers = useStudioStream("printers", marketId);
  let marketDetails = useStudioStream("market", marketId);

  // Get lot attributes
  let attributes = useSaleAttributeControllers();

  // Add useStudio hook
  let { print } = useStudio();

  // Set initial available columns
  useEffect(() => {
    let columns = [...lotBaseFields];

    let standardColumns = [
      "lotNumber",
      "sellerName",
      "generated.description",
      "catalogueDetailVATApplicable",
      "reservePriceInCents",
      "unitPriceInCents",
      "buyerName",
    ];

    // Add attribute columns
    attributes.forEach((attr) => {
      columns.push(attr.definition.id);
    });

    setAvailableColumns(columns);

    // Remove catalogueDetailVATApplicable if this is not available
    let hasVATRegisteredAttribute = attributes.find(
      (a) => a.definition.id === "catalogueDetailVATApplicable"
    );

    // Set default selected columns
    setSelectedColumns(
      standardColumns
        .filter((c) => columns.includes(c))
        .filter((c) => {
          if (!hasVATRegisteredAttribute) {
            return c !== "catalogueDetailVATApplicable";
          }
          return true;
        })
    );
  }, [attributes]);

  // Set default printer
  useEffect(() => {
    if (printers && printers.length > 0) {
      let lastPrinter = localStorage.getItem("last-used-printer");
      let printer = printers.find((p) => p.id === lastPrinter) || printers[0];
      setSelectedPrinter(printer);
    }
  }, [printers]);

  // Set default lot range based on selection only when range is enabled
  useEffect(() => {
    if (!isRangeEnabled) {
      // Keep inputs disabled and potentially clear them visually or leave as is
      setLotStart("");
      setLotEnd("");
    }
    // Remove the population logic from here, as it's now handled in handleRangeToggle
    // when isRangeEnabled becomes true.
  }, [isRangeEnabled]); // Only depend on isRangeEnabled

  // Handle range enable/disable toggle
  const handleDoubleSpaced = (checked: boolean) => {
    setIsDoubleSpaced(checked);
  }; // Depend on filteredLotsRef

  // Handle range enable/disable toggle
  const handleRangeToggle = useCallback(
    (checked: boolean) => {
      setIsRangeEnabled(checked);
      if (checked) {
        // Populate with min/max from filtered lots when enabled
        if (filteredLotsRef.current.length > 0) {
          // Find min and max lot numbers numerically/lexicographically
          const lotNumbers = filteredLotsRef.current
            .map((lot) => lot.lotNumber)
            .filter((ln) => ln != null) as string[]; // Filter out nulls/undefineds

          if (lotNumbers.length > 0) {
            // Basic sort (might need adjusting for complex lot numbers like '1A')
            lotNumbers.sort((a, b) => {
              const numA = parseFloat(a);
              const numB = parseFloat(b);
              if (!isNaN(numA) && !isNaN(numB)) {
                return numA - numB;
              }
              return a.localeCompare(b); // Fallback to string comparison
            });
            setLotStart(lotNumbers[0]);
            setLotEnd(lotNumbers[lotNumbers.length - 1]);
          } else {
            // No valid lot numbers found
            setLotStart("");
            setLotEnd("");
          }
        } else {
          // No lots in the filtered list
          setLotStart("");
          setLotEnd("");
        }
      } else {
        // Clear fields when disabled (handled by useEffect now)
        // setLotStart("");
        // setLotEnd("");
      }
    },
    [filteredLotsRef]
  ); // Depend on filteredLotsRef

  // Handle column removal
  const removeColumn = useCallback((columnId: string) => {
    setSelectedColumns((prev) => prev.filter((id) => id !== columnId));
  }, []);

  // Toggle column selection
  const toggleColumn = useCallback((columnId: string) => {
    setSelectedColumns((prev) => {
      if (prev.includes(columnId)) {
        return prev.filter((id) => id !== columnId);
      } else {
        return [...prev, columnId];
      }
    });
  }, []);

  // Handle column reordering
  const onDragEnd = useCallback((event: any) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setSelectedColumns((columns) => {
        const oldIndex = columns.indexOf(active.id);
        const newIndex = columns.indexOf(over.id);
        return arrayMove(columns, oldIndex, newIndex);
      });
    }
  }, []);

  // Get column name from ID
  const getColumnName = useCallback(
    (columnId: string) => {
      if (lotBaseFields.includes(columnId)) {
        // Special case for generated.description
        if (columnId === "generated.description") {
          return "Description";
        }

        // Special case for lotNumber
        if (columnId === "lotNumber") {
          return "Lot";
        }

        // Special case for unitPriceInCents
        if (columnId === "unitPriceInCents") {
          return "Price";
        }

        // Special case for reservePriceInCents
        if (columnId === "reservePriceInCents") {
          return "Reserve";
        }

        // Format base field name (convert camelCase to Title Case)
        let name = columnId
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (str) => str.toUpperCase());

        if (name.toLocaleLowerCase().endsWith(" in cents")) {
          name = name.slice(0, -9);
        }

        return name;
      } else {
        // Find attribute name
        const attr = attributes.find((a) => a.definition.id === columnId);
        return attr ? attr.definition.name : columnId;
      }
    },
    [attributes, lotBaseFields]
  );

  const pdfWidth = 297 - 20;

  const { currentTableWidth, remainingSpace } = useMemo(() => {
    let totalWidth = 0;
    selectedColumns.forEach((columnId) => {
      totalWidth += getColumnWidth(columnId);
    });

    // Remaining space
    let remainingSpace = pdfWidth - totalWidth;

    return { currentTableWidth: totalWidth, remainingSpace };
  }, [selectedColumns, getColumnWidth, pdfWidth]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const tableData = useMemo(() => {
    let sortedLots = sortByLotNumber(filteredLotsRef.current);

    // If we have a range, filter the lots
    if (isRangeEnabled) {
      sortedLots = getLotsInRange(filteredLotsRef.current, lotStart, lotEnd);
    }

    const tableHead: {
      dataKey: string;
      header: string;
      style?: Partial<Styles>;
    }[] = [];
    const tableBody: { [key: string]: string | number | null }[] = [];

    const columnStyles: { [key: string]: Partial<Styles> } = {};

    // Now we want to format the data for the grid
    sortedLots.map((lot) => {
      // Add the columns to the table body
      let row: { [key: string]: string | number | null } = {};
      selectedColumns.forEach((columnId) => {
        switch (columnId) {
          case "lotNumber":
            row[columnId] = lot.lotNumber ?? "";
            break;
          case "sellerName":
            row[columnId] = lot.seller?.displayName ?? "";
            break;
          case "buyerName":
            row[columnId] = lot.buyer?.displayName ?? "";
            break;
          case "title":
          case "comments":
          case "catalogueDetailVATApplicable":
            row[columnId] = lot.attributes?.[columnId] ?? "";
            break;
          case "unitPriceInCents":
            row[columnId] =
              formatAsCurrency(lot.currency, lot.unitPriceInCents) ?? "";
            break;
          case "reservePriceInCents":
            row[columnId] =
              formatAsCurrency(lot.currency, lot.reservePriceInCents) ?? "";
            break;
          case "generated.description":
            row[columnId] = lot.generated?.description ?? "";
            break;
          default:
            row[columnId] = "Not Implemented";
            break;
        }
      });

      // Add the row to the table body
      tableBody.push(row);
    });

    // If we are
    let tableSmall = currentTableWidth < pdfWidth;

    // Get the column names
    selectedColumns.forEach((columnId) => {
      tableHead.push({
        dataKey: columnId,
        header: getColumnName(columnId),
      });

      columnStyles[columnId] = {
        cellWidth: getColumnWidth(columnId),
        minCellWidth: getColumnWidth(columnId),
      };
    });

    if (tableSmall) {
      // We need to either find comments or title and expand the column if they are not there then expand the last column
      let found = false;

      selectedColumns.forEach((columnId, index) => {
        if (["comments", "title", "generated.description"].includes(columnId)) {
          found = true;

          columnStyles[columnId] = {
            ...columnStyles[columnId],
            minCellWidth: undefined,
            cellWidth: "auto",
          };
        }
      });

      // If we didn't find any, then we need to expand the last column
      if (!found) {
        let lastColumn = selectedColumns[selectedColumns.length - 1];
        columnStyles[lastColumn] = {
          ...columnStyles[lastColumn],
          minCellWidth: undefined,
          cellWidth: "auto",
        };
      }
    }

    return { tableBody, tableHead, columnStyles };
  }, [
    selectedColumns,
    lotStart,
    lotEnd,
    isRangeEnabled,
    filteredLotsRef,
    getColumnName,
    getColumnWidth,
    currentTableWidth,
    pdfWidth,
  ]);

  // Handle print action
  const handlePrint = useCallback(async () => {
    if (!selectedPrinter) {
      toast.error("Please select a printer");
      return;
    }

    if (!sale || !marketDetails || !marketId) {
      toast.error("Sale or market information is missing");
      return;
    }

    setIsLoading(true);
    let handle = toast.loading("Printing clerking sheets");

    try {
      const windowLocation = window.location.origin;

      // Construct the URL for the clerking sheets document
      // Format: /api/{marketId}/documents/clerking-sheets/{saleId}?columns=...&lotStart=...&lotEnd=...
      let url = new URL(
        `${windowLocation}/api/${marketId}/documents/clerking-sheets/${sale.id}`
      );

      // Add columns parameter
      url.searchParams.append("columns", selectedColumns.join(","));

      // Add lot range if enabled
      if (isRangeEnabled && lotStart && lotEnd) {
        url.searchParams.append("lotStart", lotStart);
        url.searchParams.append("lotEnd", lotEnd);
      }

      // Add doubleSpaced parameter if needed
      if (isDoubleSpaced) {
        url.searchParams.append("doubleSpaced", "true");
      }

      // Create print job
      let printJob = {
        id: `clerking-sheets-${sale.id}-${Date.now()}`,
        createdAt: Timestamp.now(),
        url: url.toString(),
        deviceId: selectedPrinter.id,
        copies: 1,
        isCheque: false,
      };

      console.log("Printing clerking sheets", { printJob });
      let response = await print(marketId, [printJob]);

      toast.dismiss(handle);
      console.log("Print response", response);

      let hasErrors = response.some((r) => r.result.status === "error");

      if (hasErrors) {
        response.forEach((r) => {
          if (r.result.status === "error") {
            let message = `Unable to print clerking sheets: ${r.result.message}`;
            toast.error(message);
          }
        });
        console.error(response);
      } else {
        toast.success("Clerking sheets sent to printer");

        // Save selected printer to localStorage
        localStorage.setItem("last-used-printer", selectedPrinter.id);

        // Close dialog after successful print
        close();
      }
    } catch (error) {
      toast.dismiss(handle);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "An error occurred while printing";
      toast.error(errorMessage);
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedPrinter,
    close,
    sale,
    marketId,
    marketDetails,
    selectedColumns,
    isRangeEnabled,
    lotStart,
    lotEnd,
    isDoubleSpaced,
    print,
  ]);

  // Handle opening the PDF in a new tab
  const handleOpen = useCallback(() => {
    if (!sale || !marketDetails || !marketId) {
      toast.error("Sale or market information is missing");
      return;
    }

    const windowLocation = window.location.origin;

    // Construct the URL for the clerking sheets document
    // Format: /api/{marketId}/documents/clerking-sheets/{saleId}?columns=...&lotStart=...&lotEnd=...
    let url = new URL(
      `${windowLocation}/api/${marketId}/documents/clerking-sheets/${sale.id}`
    );

    // Add columns parameter
    url.searchParams.append("columns", selectedColumns.join(","));

    // Add lot range if enabled
    if (isRangeEnabled && lotStart && lotEnd) {
      url.searchParams.append("lotStart", lotStart);
      url.searchParams.append("lotEnd", lotEnd);
    }

    // Add doubleSpaced parameter if needed
    if (isDoubleSpaced) {
      url.searchParams.append("doubleSpaced", "true");
    }

    // Open the URL in a new tab
    window.open(url.toString(), "_blank");
  }, [
    sale,
    marketId,
    marketDetails,
    selectedColumns,
    isRangeEnabled,
    lotStart,
    lotEnd,
    isDoubleSpaced,
  ]);

  // Handle download action
  const handleDownload = useCallback(async () => {
    if (!sale || !marketDetails) {
      return;
    }

    setIsLoading(true);
    try {
      // In a real implementation, this would generate and download the PDF
      // Currently just a placeholder that will be implemented in the next task
      toast.success("PDF downloaded");

      //

      downloadPDF({
        saleTitle: sale.name,
        saleDate: sale.startsAt,
        reportTitle: "Price Entry Sheets",
        marketDetails: {
          logo: marketDetails.logo,
          name: marketDetails.name,
          email: marketDetails.email,
          phone: marketDetails.telephone,
          headerFillColor: marketDetails.secondaryColour,
        },
        data: tableData.tableBody,
        columnStyles: tableData.columnStyles,
        bodyStyles: {
          minCellHeight: isDoubleSpaced ? 17 : 0,
          valign: "middle",
        },
        headStyles: {
          valign: "bottom",
        },
        headers: tableData.tableHead,
        filename: `${sale.name}-price-entry-sheets.pdf`,
      });

      //
    } catch (error) {
      toast.error("Failed to download PDF");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [
    downloadPDF,
    tableData,
    selectedColumns,
    sale,
    marketDetails,
    isDoubleSpaced,
  ]);

  return (
    <DialogChrome isOpen={isOpen} close={close}>
      <div className="bg-black rounded-lg px-5 py-4">
        {/* Header */}
        <div className="flex flex-row items-center">
          <ClipboardDocumentIcon className="h-6 w-6 text-martEye-400" />
          <DialogTitle
            as="h3"
            className="text-base font-bold leading-6 text-white flex-grow ml-2 select-none"
          >
            Price Entry Sheets
          </DialogTitle>

          <button
            type="button"
            className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-white/40 text-white hover:bg-white/10"
            onClick={close}
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col gap-4 mt-4 h-[24rem]">
          {/* Two columns */}
          <div className="flex flex-row flex-grow">
            {/* Left side - Lot range and column selection */}
            <div className="flex-grow ml-1 mt-2 pr-4">
              {/* Column Selection */}
              <div>
                <h4 className="text-sm font-semibold text-[#CBCBCB] mb-2 flex justify-between items-center select-none">
                  <span>Columns</span>
                  <ColumnSelector
                    availableColumns={availableColumns}
                    selectedColumns={selectedColumns}
                    onAddColumn={toggleColumn}
                    getColumnName={getColumnName}
                    getColumnWidth={getColumnWidth}
                    remainingSpace={remainingSpace}
                  />
                </h4>

                {/* <div className="overflow-hidden rounded-full bg-gray-200 my-2">
                  <div
                    style={{
                      width: `${(currentTableWidth / pdfWidth) * 100}%`,
                    }}
                    className={classNames(
                      "h-2 rounded-full",

                      currentTableWidth / pdfWidth > 0.9
                        ? "bg-danger-500"
                        : "bg-martEye-400"
                    )}
                  />
                </div> */}

                {/* Selected Columns */}
                <div className="border border-gray-200/30 rounded-md p-2 mb-1 flex flex-col flex-grow">
                  <h5 className="text-xs font-semibold text-[#CBCBCB]/70 mb-2 select-none">
                    Selected Columns
                  </h5>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={onDragEnd}
                  >
                    <SortableContext
                      items={selectedColumns}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="flex flex-col space-y-1 max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent">
                        {selectedColumns.map((columnId) => (
                          <SortableColumnItem
                            key={columnId}
                            id={columnId}
                            name={getColumnName(columnId)}
                            onRemove={() => removeColumn(columnId)}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>
              </div>
            </div>

            {/* Right side - Printer selection, lot range and actions */}
            <div className="flex flex-col w-80 pl-6 py-3 border-l border-gray-200/30">
              <div className="mb-6">
                <PrinterSelector
                  printers={printers || []}
                  selected={selectedPrinter}
                  setSelected={setSelectedPrinter}
                />
              </div>

              {/* Lot Range */}
              <div className="mb-6">
                <div className="flex items-center mb-3">
                  {/* Range Enable Checkbox */}
                  <input
                    id="range-enable"
                    type="checkbox"
                    checked={isRangeEnabled}
                    onChange={(e) => handleRangeToggle(e.target.checked)}
                    className="h-4 w-4 rounded border-[#545454] text-martEye-400 focus:ring-martEye-400 bg-[#545454] cursor-pointer mr-2"
                  />
                  <h4 className="text-sm font-semibold text-[#CBCBCB] select-none">
                    Lot Range
                  </h4>
                </div>

                <div className="flex items-center gap-2">
                  <div className="w-1/2">
                    <input
                      type="text"
                      value={lotStart}
                      onChange={(e) => setLotStart(e.target.value)}
                      disabled={!isRangeEnabled} // Disabled when checkbox is UNCHECKED
                      placeholder="Start" // Add placeholder
                      className={classNames(
                        "block w-full rounded-md border-0 py-1.5 px-3 text-white ring-1 ring-inset ring-gray-200/30 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6 bg-transparent", // Adjusted placeholder color
                        !isRangeEnabled ? "opacity-50 cursor-not-allowed" : ""
                      )}
                    />
                  </div>
                  <div className="w-1/2">
                    <input
                      type="text"
                      value={lotEnd}
                      onChange={(e) => setLotEnd(e.target.value)}
                      disabled={!isRangeEnabled} // Disabled when checkbox is UNCHECKED
                      placeholder="End" // Add placeholder
                      className={classNames(
                        "block w-full rounded-md border-0 py-1.5 px-3 text-white ring-1 ring-inset ring-gray-200/30 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6 bg-transparent", // Adjusted placeholder color
                        !isRangeEnabled ? "opacity-50 cursor-not-allowed" : ""
                      )}
                    />
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <div className="flex items-center mb-3">
                  {/* Range Enable Checkbox */}
                  <input
                    id="doubleSpaced"
                    type="checkbox"
                    checked={isDoubleSpaced}
                    onChange={(e) => handleDoubleSpaced(e.target.checked)}
                    className="h-4 w-4 rounded border-[#545454] text-martEye-400 focus:ring-martEye-400 bg-[#545454] cursor-pointer mr-2"
                  />
                  <h4 className="text-sm font-semibold text-[#CBCBCB] select-none">
                    Large Rows
                  </h4>
                </div>
              </div>

              <div className="mt-auto">
                <ActionButtons
                  isLoading={isLoading}
                  onPrint={handlePrint}
                  onDownload={handleDownload}
                  onOpen={handleOpen}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </DialogChrome>
  );
}

// Sortable column item
function SortableColumnItem({
  id,
  name,
  onRemove,
}: {
  id: string;
  name: string;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Check if this is the lot number column which is required
  const isLotNumber = id === "lotNumber";

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center justify-between bg-gray-800/50 rounded p-2 group"
    >
      <div
        className="flex items-center gap-2 flex-grow cursor-move select-none"
        {...attributes}
        {...listeners}
      >
        <span className="text-sm text-white select-none">{name}</span>
      </div>
      {!isLotNumber && (
        <button
          type="button"
          onClick={onRemove}
          className="text-gray-400 hover:text-white"
        >
          <TrashIcon className="h-4 w-4 opacity-60 hover:opacity-100" />
        </button>
      )}
    </div>
  );
}

// Column selector
function ColumnSelector({
  availableColumns,
  selectedColumns,
  onAddColumn,
  getColumnName,
  getColumnWidth,
  remainingSpace,
}: {
  availableColumns: string[];
  selectedColumns: string[];
  onAddColumn: (columnId: string) => void;
  getColumnName: (columnId: string) => string;
  getColumnWidth: (columnId: string) => number;
  remainingSpace: number;
}) {
  const [searchQuery, setSearchQuery] = useState("");

  const toggleColumn = useCallback(
    (columnId: string) => {
      if (selectedColumns.includes(columnId)) {
        // If already selected, remove it (handled by parent)
        onAddColumn(columnId);
      } else {
        // If not selected, add it
        onAddColumn(columnId);
      }
    },
    [selectedColumns, onAddColumn]
  );

  const filteredColumns = searchQuery
    ? availableColumns.filter((columnId) =>
        getColumnName(columnId)
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      )
    : availableColumns;

  return (
    <div className="relative">
      <Popover className="relative">
        {({ open, close }: { open: boolean; close: () => void }) => (
          <>
            <PopoverButton className="flex h-6 w-6 items-center justify-center rounded-full bg-green-600 text-white hover:bg-green-500 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2">
              <PlusIcon className="h-4 w-4" aria-hidden="true" />
              <span className="sr-only">Add column</span>
            </PopoverButton>

            {/* Overlay when popover is open */}
            {open && (
              <div
                className="fixed inset-0 bg-black/50 z-10"
                onClick={() => {
                  close();
                  setSearchQuery(""); // Reset search query when closing via overlay click
                }}
              />
            )}

            <Transition
              show={open}
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-x-1"
              enterTo="opacity-100 translate-x-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-x-0"
              leaveTo="opacity-0 translate-x-1"
              afterLeave={() => setSearchQuery("")} // Reset search query after transition completes
            >
              <PopoverPanel className="absolute z-20 mt-1 right-0 w-64 max-h-80 overflow-hidden border border-gray-200/30 rounded-md p-2 bg-black shadow-lg flex flex-col">
                {/* Search Input - Remains Fixed */}
                <div className="mb-2 flex-shrink-0">
                  <div className="relative">
                    <MagnifyingGlassIcon className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search columns..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="block w-full rounded-md border-0 py-1.5 pl-8 pr-3 text-white bg-gray-800/50 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm"
                    />
                  </div>
                </div>

                {/* Scrollable Column List */}
                <div className="flex-grow overflow-y-auto">
                  {filteredColumns.length === 0 ? (
                    <p className="text-sm text-gray-400 py-2 text-center">
                      No matching columns
                    </p>
                  ) : (
                    <ul className="divide-y divide-gray-200/30">
                      {filteredColumns.map((columnId) => {
                        const isSelected = selectedColumns.includes(columnId);
                        const columnWidth = getColumnWidth(columnId);

                        const hasRoom =
                          remainingSpace < columnWidth && !isSelected;

                        return (
                          <li key={columnId} className="py-1">
                            <button
                              type="button"
                              onClick={() => {
                                toggleColumn(columnId);
                              }}
                              className={classNames(
                                "w-full text-left text-sm px-2 py-1 rounded flex items-center justify-between select-none",
                                isSelected
                                  ? "text-white/80 bg-gray-800/30"
                                  : hasRoom
                                  ? "text-white/40 cursor-not-allowed"
                                  : "text-white hover:text-martEye-400 hover:bg-gray-800/50"
                              )}
                              disabled={
                                // If the column will not fit in the remaining space, disable it
                                hasRoom
                              }
                              title={hasRoom ? "Not enough space" : ""}
                            >
                              <span>{getColumnName(columnId)}</span>

                              {isSelected && (
                                <CheckIcon
                                  className="h-4 w-4 text-green-500"
                                  aria-hidden="true"
                                />
                              )}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </PopoverPanel>
            </Transition>
          </>
        )}
      </Popover>
    </div>
  );
}

// Printer selector
function PrinterSelector({
  printers,
  selected,
  setSelected,
}: {
  printers: Printer[];
  selected: Printer | null;
  setSelected: (printer: Printer) => void;
}) {
  return (
    <Listbox value={selected} onChange={setSelected}>
      {({ open }) => (
        <>
          <Label className="block text-sm font-normal leading-6 text-[#BDBDBD] ml-1 select-none">
            Printer
          </Label>
          <div className="relative mt-0">
            <ListboxButton className="relative w-full cursor-default rounded-md bg-transparent py-1.5 pl-3 pr-10 text-left text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300/30 focus:outline-none focus:ring-2 focus:ring-green-600 sm:text-sm sm:leading-6">
              <span className="flex items-center">
                {selected ? (
                  <span className="bg-gray-300 inline-block h-2 w-2 flex-shrink-0 rounded-full" />
                ) : null}
                <span
                  className={classNames(
                    "block truncate",
                    selected ? "ml-3" : "ml-0 text-white/70"
                  )}
                >
                  {selected?.friendlyName || "Select a printer"}
                </span>
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronUpDownIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </span>
            </ListboxButton>

            <ListboxOptions className="absolute z-10 mt-1 max-h-60 w-full border border-gray-300/50 overflow-auto rounded-md bg-black py-1 text-base shadow-lg ring-1 ring-red-100 ring-opacity-5 focus:outline-none sm:text-sm">
              {printers.map((printer) => (
                <ListboxOption
                  key={printer.id}
                  className={({ active }) =>
                    classNames(
                      active ? "bg-green-600 text-white" : "",
                      !active ? "text-gray-100" : "",
                      "relative cursor-default select-none py-2 pl-3 pr-9"
                    )
                  }
                  value={printer}
                >
                  {({ selected, active }) => (
                    <>
                      <div className="flex items-center">
                        <span
                          className="bg-gray-300 inline-block h-2 w-2 flex-shrink-0 rounded-full"
                          aria-hidden="true"
                        />
                        <span
                          className={classNames(
                            selected ? "font-semibold" : "font-normal",
                            "ml-3 block truncate"
                          )}
                        >
                          {printer.friendlyName}
                        </span>
                      </div>

                      {selected ? (
                        <span
                          className={classNames(
                            active ? "text-white" : "text-martEye-400",
                            "absolute inset-y-0 right-0 flex items-center pr-4"
                          )}
                        >
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      ) : null}
                    </>
                  )}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </>
      )}
    </Listbox>
  );
}

// Action buttons
function ActionButtons({
  isLoading,
  onPrint,
  onDownload,
  onOpen,
}: {
  isLoading: boolean;
  onPrint: () => void;
  onDownload: () => void;
  onOpen: () => void;
}) {
  return (
    <div className="flex flex-col gap-2 mt-4">
      <div className="flex flex-row gap-2">
        <button
          type="button"
          className="flex-1 relative inline-flex items-center justify-center gap-x-1 rounded-lg bg-transparent border border-gray-200/30 px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent"
          onClick={onOpen}
          disabled={isLoading}
        >
          <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
          Open
        </button>

        <button
          type="button"
          className="flex-1 relative inline-flex items-center justify-center gap-x-1 rounded-lg bg-transparent border border-gray-200/30 px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent"
          onClick={onDownload}
          disabled={isLoading}
        >
          <ArrowDownTrayIcon className="h-4 w-4" aria-hidden="true" />
          Download
        </button>
      </div>

      <button
        type="button"
        className="relative inline-flex items-center justify-center gap-x-3 rounded-lg bg-green-600 px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-green-600"
        onClick={onPrint}
        disabled={isLoading}
      >
        {isLoading && (
          <div className="absolute right-3 h-5 w-5 flex items-center justify-center">
            <Spinner />
          </div>
        )}
        <PrinterIcon className="absolute left-3 h-5 w-5" aria-hidden="true" />
        Print
      </button>
    </div>
  );
}

// Dialog chrome
function DialogChrome(props: {
  isOpen: boolean;
  close: () => void;
  children: ReactNode;
}) {
  let { isOpen, close, children } = props;

  return (
    <Transition show={isOpen === true} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-60"
        initialFocus={undefined}
        onClose={close}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-60 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all w-full max-w-3xl">
                <Transition
                  show={isOpen}
                  as={Fragment}
                  enter="transition ease-out duration-200 delay-75"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="transition ease-in duration-150"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="transition-opacity">{children}</div>
                </Transition>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

// Helpers
export function getColumnWidth(columnId: string): number {
  switch (columnId) {
    case "lotNumber":
    case "productCode":
      return 15;

    case "title":
    case "comments":
    case "generated.description":
      return 60;

    case "sellerName":
    case "buyerName":
      return 50;

    default:
      return 25;
  }
}
